import { AlertCircle, FileUp, Loader2 } from 'lucide-react';
import { DEFAULT_TEXT_STYLE } from '@tes/domain';
import { FieldPreview } from '../../editor/FieldPreview';
import { FieldBox, PageCanvas } from '../../editor/PageCanvas';
import { INPUT_TOOLS } from '../../editor/ToolPalette';
import { useFormatDate } from '../../lib/useSettings';
import { useQuickSend } from './quickSendStore';

/**
 * The open document with its fields — the template editor's canvas and field
 * boxes over the local PDF file, so placement here stamps exactly like a
 * template's would.
 */
export function QuickSendCanvas() {
  const doc = useQuickSend((s) => s.docs.find((d) => d.id === s.activeId) ?? null);
  const page = useQuickSend((s) => s.page);
  const zoom = useQuickSend((s) => s.zoom);
  const selectedUid = useQuickSend((s) => s.selectedUid);
  const signerName = useQuickSend((s) => s.name);
  const { setPage, setZoom, select, addField, updateField } = useQuickSend.getState();
  // Date fields preview in the platform's real format (e.g. DD/MM/YYYY).
  const { pattern: datePattern } = useFormatDate();

  if (!doc) {
    return (
      <Placeholder>
        <FileUp size={28} />
        <span>Add a PDF on the left, then place the fields your contact fills in.</span>
      </Placeholder>
    );
  }
  if (doc.state === 'uploading') {
    return (
      <Placeholder>
        <Loader2 size={24} className="animate-spin" />
        <span>Uploading {doc.file.name}…</span>
      </Placeholder>
    );
  }
  if (doc.state === 'failed') {
    return (
      <Placeholder>
        <AlertCircle size={24} className="text-red-500" />
        <span className="text-red-600">{doc.error}</span>
        <span>Remove it and try a different file.</span>
      </Placeholder>
    );
  }

  const size = doc.pageSizes[page - 1];
  return (
    <PageCanvas
      // A different document is a different PDF — start its viewer fresh.
      key={doc.id}
      file={doc.file}
      page={page}
      pageCount={doc.pageSizes.length}
      onPageChange={setPage}
      zoom={zoom}
      onZoomChange={setZoom}
      size={size}
      onDeselect={() => select(null)}
      toolbarExtra={
        <>
          <div className="mx-1 h-5 w-px bg-slate-200" />
          <span className="text-xs text-slate-400">Add:</span>
          {INPUT_TOOLS.map(({ label, type, icon: Icon }) => (
            <button
              key={type}
              type="button"
              onClick={() => addField(type)}
              title={`Add ${label.toLowerCase()} to this page`}
              className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:border-brand-300 hover:bg-brand-50"
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </>
      }
    >
      {doc.fields
        .filter((f) => f.page === page)
        .map((f) => (
          <FieldBox
            key={f.uid}
            field={f}
            zoom={zoom}
            selected={selectedUid === f.uid}
            onSelect={() => select(f.uid)}
            onChange={(geometry) => updateField(f.uid, geometry)}
          >
            <FieldPreview
              field={f}
              zoom={zoom}
              style={DEFAULT_TEXT_STYLE}
              datePattern={datePattern}
              roleLabel={signerName.trim() || 'Signer'}
            />
          </FieldBox>
        ))}
    </PageCanvas>
  );
}

function Placeholder({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 bg-slate-50 p-8 text-center text-sm text-slate-400">
      {children}
    </div>
  );
}
